import { ICONS } from 'consts/consts';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button, Col, PopoverBody, Row, UncontrolledPopover } from 'reactstrap';
import { BasicPerson } from 'types';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { renderValidationError } from 'utils/util/formatter';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import FilterablePeopleTable, {
  TALBE_SIZE_NO_LIMIT,
} from 'views/Widgets/People/FilterablePeopleTable';
import { FORMAT_AVATAR_WITH_TITLE } from 'views/Widgets/People/Filters/common';
import Scrollable from 'views/Widgets/Scrollable';
import { useCampaignReports } from '../CampaignReportsContext';
import {
  ApiCampaignReport,
  ApiCampaignReportItem,
  CampaignReportItemStatus,
  formatCampaignReportDescriptor,
} from './EngagementSurvey';

interface Props {
  reportId: number;
  isOpen: boolean;
  onClosed: () => void;
  onCallback: () => void;
}

type ReportData = {
  description: ApiCampaignReport;
  items: ApiCampaignReportItem[];
};

type CampaignCustomReportItemNotification = {
  campaign_report_item_id: number;
  people_to_notify: number[];
};

// Interface for a notification row in the table
interface NotificationRow {
  id: string; // Composite ID for the row (itemId-personId)
  slice: string; // Item name (Report name)
  person: BasicPerson;
  alreadyNotified: boolean; // Whether the person is already in people_notified
  isNotified: boolean; // Whether the person should be notified (for the checkbox)
  slice_sort: string; // For sorting by slice name
  person_sort: string; // For sorting by person name
  already_notified: React.ReactNode; // The already notified badge node
}

const EngagementSurveyReportNotificationModalEditor: FC<Props> = ({
  reportId,
  isOpen,
  onClosed,
  onCallback,
}) => {
  const { formatMessage } = useIntl();
  const { campaignId } = useCampaignReports();
  const proxyParameters = useProxyParameters();

  const [object, setObject] = useState<ReportData | null>(null);
  const [selectedRowIdsSet, setSelectedRowIdsSet] = useState<Set<string>>(
    new Set()
  );

  // Filter published items
  const publishedItems = useMemo(() => {
    return (
      object?.items?.filter(
        (item) => item.status === CampaignReportItemStatus.PUBLISHED
      ) || []
    );
  }, [object?.items]);

  // Compute unique people map by item ID
  const uniquePeopleMapByItem = useMemo(() => {
    const map = new Map<number, Map<number, BasicPerson>>();

    publishedItems.forEach((item) => {
      const allPeople = [...item.owners, ...item.viewers];
      const uniquePeopleMap = new Map<number, BasicPerson>();

      allPeople.forEach((person) => {
        if (person.id !== undefined && !uniquePeopleMap.has(person.id)) {
          uniquePeopleMap.set(person.id, person);
        }
      });
      map.set(item.id, uniquePeopleMap);
    });

    return map;
  }, [publishedItems]);

  // Simple callback to store the retrieved data
  const onSuccesfullRetrieve = useCallback(
    (
      data: ReportData | null,
      error: unknown,
      hardErrorMessage: unknown
    ): void => {
      if (!error && !hardErrorMessage && data) {
        setObject(data);
      }
    },
    []
  );

  // API call to get report data
  const { error: errorForReportData } = useConfirmApi<ReportData>({
    url: `/campaigns/${campaignId}/reports/${reportId}`,
    method: 'GET',
    clearOnChange: true,
    clearOnError: true,
    disabled: !isOpen,
    callback: onSuccesfullRetrieve,
  });

  const handleOnSubmit = useCallback(
    (submittedObject, onSubmit) => {
      const payloadsByItemId: Record<number, number[]> = {};

      // Collect all checked rows from selectedRowIdsSet
      Array.from(selectedRowIdsSet).forEach((rowId) => {
        const [itemId, personId] = rowId.split('-');
        const item = publishedItems.find((item) => item.id === Number(itemId));
        if (item) {
          if (!payloadsByItemId[itemId]) {
            payloadsByItemId[itemId] = [];
          }
          payloadsByItemId[itemId].push(Number(personId));
        }
      });

      // Create a map of item ID to array of person IDs to notify
      const notificationsDataPayload: CampaignCustomReportItemNotification[] =
        Object.entries(payloadsByItemId).map(([itemId, peopleToNotify]) => ({
          campaign_report_item_id: Number(itemId),
          people_to_notify: peopleToNotify,
        }));

      // Wrap the notifications map in a 'notifications' object
      // so that organization_id and proxy can go along nicely.
      const payload = { notifications: notificationsDataPayload };
      ConfirmAPI.sendRequestToConfirm(
        'POST',
        `/campaigns/${campaignId}/report-items/notify`,
        { ...proxyParameters, ...payload },
        (data, error, hardErrorMessage) => {
          if (!error && !hardErrorMessage && data) {
            if (data.message) {
              toast.success(data.message);
            }
            onCallback();
          }
          onSubmit(data, error, hardErrorMessage);
        }
      );
    },
    [selectedRowIdsSet, publishedItems, campaignId, onCallback, proxyParameters]
  );

  // Close handler - reset state and call the provided callback
  const handleOnClosed = useCallback(() => {
    // Reset all state to initial values
    setObject(null);
    setSelectedRowIdsSet(new Set());
    onClosed();
  }, [onClosed]);

  // Update to handle checkbox changes and update state
  const toggleSelectedRowField = useCallback(
    (rowId: string) => {
      // Update the selectedRowIdsSet for FilterablePeopleTable
      if (selectedRowIdsSet.has(rowId)) {
        setSelectedRowIdsSet(
          new Set(Array.from(selectedRowIdsSet).filter((id) => id !== rowId))
        );
      } else {
        setSelectedRowIdsSet(
          new Set([...Array.from(selectedRowIdsSet), rowId])
        );
      }
    },
    [selectedRowIdsSet]
  );

  // Format boolean values with checkmark for true and nothing for false
  const formatBoolean = useCallback((value: boolean) => {
    return value ? <i className={`${ICONS.COMPLETE}`} /> : null;
  }, []);

  const rows = useMemo(() => {
    if (!publishedItems.length) return [];

    const notificationRows: NotificationRow[] = [];

    // Process each published item (report)
    publishedItems.forEach((item) => {
      // Get unique people for this item
      const uniquePeopleMap = uniquePeopleMapByItem.get(item.id);
      if (!uniquePeopleMap) return;

      // Create a map of already notified people.
      const notifiedPeopleMap = new Map<number | undefined, boolean>();
      (item.people_notified || []).forEach((person) => {
        if (person.id !== undefined) {
          notifiedPeopleMap.set(person.id, true);
        }
      });

      // Create a row for each unique person (from owners and viewers)
      uniquePeopleMap.forEach((person, personId) => {
        // Check if this person is in the people_notified list
        const isAlreadyNotified = notifiedPeopleMap.has(personId);
        const rowId = `${item.id}-${personId}`;

        // Get the current checked state from selectedRowIdsSet
        const isChecked = selectedRowIdsSet.has(rowId);

        notificationRows.push({
          id: rowId,
          slice: item.name,
          person: person,
          alreadyNotified: isAlreadyNotified,
          isNotified: isChecked,
          slice_sort: item.name.toLowerCase(),
          person_sort: person.full_name.toLowerCase(),
          already_notified: (
            <div className="text-center py-0">
              {formatBoolean(isAlreadyNotified)}
            </div>
          ),
        });
      });
    });

    // Sort rows by slice name first, then by person name
    return notificationRows;
  }, [selectedRowIdsSet, formatBoolean, publishedItems, uniquePeopleMapByItem]);

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.table.column.recipient.name',
          defaultMessage: 'Recipient',
        }),
        field: 'person',
        sort: 'person_sort',
        format: FORMAT_AVATAR_WITH_TITLE,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.table.column.slice.name',
          defaultMessage: 'Report Name',
        }),
        field: 'slice',
        sort: 'slice_sort',
      },
      {
        name: formatMessage({
          id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.table.column.already_notified.name',
          defaultMessage: 'Already Notified',
        }),
        field: 'already_notified',
        sort: (a, b) => {
          if (a.alreadyNotified === b.alreadyNotified) return 0;
          return a.alreadyNotified ? -1 : 1;
        },
      },
    ],
    [formatMessage]
  );

  const getUniqueRowId = (row: NotificationRow) => row.id;
  const isRowSelectable = () => true;

  const inputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.CUSTOM_INPUT,
        component: (
          <Scrollable maxHeight="600px">
            <FilterablePeopleTable
              className="mb-0 table-hover"
              rows={rows}
              columns={columns}
              hideFilters={true}
              hideExportButton={true}
              initialTableSize={TALBE_SIZE_NO_LIMIT}
              defaultSort={['slice_sort', 'person_sort']}
              getUniqueRowId={getUniqueRowId}
              disabled={false}
              toggleSelectedRowField={toggleSelectedRowField}
              selectedRowToggleFieldsSet={selectedRowIdsSet}
              setSelectedRowToggleFieldsSet={setSelectedRowIdsSet}
              isRowSelectable={isRowSelectable}
            />
          </Scrollable>
        ),
      },
    ],
    [columns, rows, toggleSelectedRowField, selectedRowIdsSet]
  );

  const renderForm = useCallback(
    (inputs, submitButton) => {
      if (!object) {
        return (
          <Loading
            message={formatMessage({
              id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.message.loading_report_details',
              defaultMessage: 'Loading report details...',
            })}
          />
        );
      }

      const reportInfo = formatCampaignReportDescriptor(
        object!.description,
        formatMessage
      );

      const customSubmitButton =
        selectedRowIdsSet.size > 0 ? (
          submitButton
        ) : (
          <span id="notification-submit-button-wrapper">
            <Button
              color="primary"
              className="w-100"
              disabled
              style={{ pointerEvents: 'none' }}
            >
              <FormattedMessage
                id="app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.submitText.notify"
                defaultMessage="Notify"
              />
            </Button>
            <UncontrolledPopover
              placement="top"
              trigger="hover"
              target="notification-submit-button-wrapper"
            >
              <PopoverBody className="text-dark">
                <FormattedMessage
                  id="app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.disabled_hover_text"
                  defaultMessage="Please select at least one recipient to notify."
                />
              </PopoverBody>
            </UncontrolledPopover>
          </span>
        );

      return (
        <>
          <Row>
            <Col>
              <h4>{reportInfo.title}</h4>
              <small className="text-muted">{reportInfo.description}</small>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col>
              <p className="mb-4">
                <FormattedMessage
                  id="app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.table.description"
                  defaultMessage="Select which recipients should be notified about this report."
                />
              </p>
              {inputs}
            </Col>
          </Row>
          {errorForReportData && (
            <Row>{renderValidationError(errorForReportData)}</Row>
          )}

          <Row className="mt-4">
            <Col>
              <Button className="w-100" color="light" onClick={handleOnClosed}>
                <FormattedMessage
                  id="app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.cancel"
                  defaultMessage="Skip"
                />
              </Button>
            </Col>
            <Col>{customSubmitButton}</Col>
          </Row>
        </>
      );
    },
    [
      formatMessage,
      handleOnClosed,
      object,
      selectedRowIdsSet.size,
      errorForReportData,
    ]
  );

  return (
    <ModalEditor
      title={formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.title.notifications',
        defaultMessage: 'Notifications',
      })}
      isOpen={isOpen}
      toggle={handleOnClosed}
      object={object}
      inputs={inputs}
      callback={onCallback}
      renderForm={renderForm}
      keepOpenOnSuccess={false}
      inline={false}
      submitPromise={handleOnSubmit}
      submitText={formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.submitText.notify',
        defaultMessage: 'Notify',
      })}
      buttonClassName=""
      onClosed={handleOnClosed}
      className="mb-0"
      disabled={selectedRowIdsSet.size === 0}
      // Add validation to prevent submission if no recipients are selected
      onValidate={() => {
        if (selectedRowIdsSet.size === 0) {
          return {
            general: formatMessage({
              id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_report_notification_modal_editor.error.no_recipients_selected',
              defaultMessage: 'Please select at least one recipient to notify.',
            }),
          };
        }
        return null;
      }}
    />
  );
};

export default React.memo(EngagementSurveyReportNotificationModalEditor);
